import {
  Alert,
  AloftDate,
  PairedCostRouteItemWithQuery,
  schAlertSeverity,
  schAlertStatus,
  schPairedCostRouteItem,
  schQueryParams,
} from '@aloft/toolkit-shared';

import { CompPropertyDataState } from '../../../../redux/types.ts';
import { isSaleDateOutOfBounds } from '../../../../utils/locationTypeSaleDateRequirementMonths.ts';
import { AlertDefinition } from '../../types.ts';
import { getAlertTriggers } from '../utils/alertTriggers.ts';
import { getScopedAlertType } from '../utils/compTypeScoping.ts';

const SALE_DATE_HARD_STOP_MONTHS = 12;

const TIP =
  'If more recent alternatives are unavailable, provide market-supported reasoning—such as limited inventory, a stable market or relevance of older sales.';

export const compOlderThan12Months: AlertDefinition = ({
  compSequence,
  fullCompSequence,
  propertyDataEntities,
  order,
  compType,
  effectiveDate,
}) => {
  return compSequence
    .map((compID, index) => {
      const compData = propertyDataEntities[compID] as CompPropertyDataState | undefined;
      if (!compData) return undefined;

      const saleMonth = compData.compData.editableData.transaction.saleDate;
      if (!saleMonth || compData.compData.editableData.transaction.status !== 'SettledSale')
        return undefined;

      const saleDate = AloftDate.fromIsoString(`${saleMonth}-01`);

      if (
        !isSaleDateOutOfBounds(saleDate, effectiveDate, SALE_DATE_HARD_STOP_MONTHS)
      ) {
        return undefined;
      }

      const compIndex = fullCompSequence?.indexOf(compID);

      const alert: Alert = {
        type: getScopedAlertType('compOlderThan12Months', compType),
        status: schAlertStatus.enum.active,
        severity: schAlertSeverity.enum.error,
        orderType: ['appraisify'],
        ...(compType ? { compType } : {}),
        message: `Comp ${compIndex !== undefined ? `${compIndex + 1}` : index + 1} has a sale date more than 12 months before the effective date.`,
        propertyID: compID,
        triggers: getAlertTriggers({
          fixRoute:
            `${schPairedCostRouteItem.enum.features}?${schQueryParams.enum.addComps}=true` as PairedCostRouteItemWithQuery,
          order,
        }),
        fixingInfo: {
          tips: TIP,
          pairedCostRouteItem:
            `${schPairedCostRouteItem.enum.features}?${schQueryParams.enum.addComps}=true` as PairedCostRouteItemWithQuery,
          fixingMethod: ['comment', 'action'],
          commentAlwaysRequired: false,
          targetCellTooltip: 'Sale more than 12 months old',
        },
      };

      return alert;
    })
    .filter((alert): alert is Alert => alert !== undefined);
};
